<template>
    <div class="page">
        <h1>Your Tank Parameters</h1>

        <section>
            <transition-group name="fade-resize" mode="out-in" tag="div">
                <div v-if="!shouldShowParametersSection" key="intro">
                    <h2>Want Suggestions?</h2>
                    <p>This tool can automatically suggest the best parameters for your chosen shrimp.</p>

                    <div class="button-container with-bottom-margin">
                        <button class="button primary" @click.prevent="skipParameters">Suggest parameters</button>
                    </div>

                    <br>

                    <p class="with-bottom-margin">Alternatively, if you know your parameters already you can set them here to find compatible shrimp.</p>

                    <div class="button-container with-bottom-margin">
                        <button class="button" @click.prevent="showParametersSection">Choose parameters</button>
                    </div>
                </div>

                <div v-if="shouldShowParametersSection" key="parameters">
                    <div class="heading-container">
                        <h2>Manual</h2>

                        <div class="button-container">
                            <button class="button" @click.prevent="skipParameters">Suggest parameters</button>
                        </div>
                    </div>

                    <p class="with-bottom-margin">Know your water parameters? Enter them here to find compatible shrimp for your setup.</p>

                    <form>
                        <div class="parameter-fields-container with-bottom-margin">
                            <span class="error" v-if="validationError"><strong>Error:</strong> {{ validationError }}</span>
                            <label>
                                <span>GH</span>
                                <input type="number" :min="validationRules.minGh" :max="validationRules.maxGh" required v-model="inputGh">
                            </label>
                            <label>
                                <span>KH</span>
                                <input type="number" :min="validationRules.minKh" :max="validationRules.maxKh" required v-model="inputKh">
                            </label>
                            <label>
                                <span>pH</span>
                                <input type="number" :min="validationRules.minPh" :max="validationRules.maxPh" step=".1" required v-model="inputPh">
                            </label>
                        </div>

                        <div class="button-container">
                            <button class="button primary" @click.prevent="setParameters">Set parameters</button>
                        </div>
                    </form>
                </div>
            </transition-group>
        </section>
    </div>
</template>

<script>
import * as ANALYTICS from '@/lib/analytics'

export default {
    name: 'TankParameters',
    data: () => {
        return {
            inputGh: null,
            inputKh: null,
            inputPh: null,
            validationRules: {
                minGh: 0,
                maxGh: 20,
                minKh: 0,
                maxKh: 20,
                minPh: 1,
                maxPh: 14,
            },
            validationError: null,
            wantsParameterSection: false,
        }
    },
    created() {
        this.inputGh = this.$store.state.inputTankParameters.gh || 5
        this.inputKh = this.$store.state.inputTankParameters.kh || 1
        this.inputPh = this.$store.state.inputTankParameters.ph || 7
    },
    methods: {
        validateInputs() {
            this.validationError = null

            if(this.inputGh < this.validationRules.minGh) {
                this.validationError = 'GH must be greater than ' + this.validationRules.minGh
            }
            else if(this.inputGh > this.validationRules.maxGh) {
                this.validationError = 'GH must be less than ' + this.validationRules.maxGh
            }
            else if(this.inputKh < this.validationRules.minKh) {
                this.validationError = 'KH must be greater than ' + this.validationRules.minKh
            }
            else if(this.inputKh > this.validationRules.maxKh) {
                this.validationError = 'KH must be less than ' + this.validationRules.maxKh
            }
            else if(this.inputPh < this.validationRules.minPh) {
                this.validationError = 'pH must be greater than ' + this.validationRules.minPh
            }
            else if(this.inputPh > this.validationRules.maxPh) {
                this.validationError = 'pH must be less than ' + this.validationRules.maxPh
            }
        },
        setParameters() {
            this.validateInputs()

            if(this.validationError !== null) {
                return
            }

            let desiredParameters = {
                gh: this.inputGh,
                kh: this.inputKh,
                ph: this.inputPh,
            }

            this.$ga.event(ANALYTICS.CATEGORY, 'Manual parameters', JSON.stringify(desiredParameters))

            this.$store.commit('SET_INPUT_TANK_PARAMETERS', desiredParameters)
            this.$router.push(this.getNextPageUri())
        },
        skipParameters() {
            let eventLabel = this.shouldShowParametersSection ? 'From parameter section' : 'From intro section'
            this.$ga.event(ANALYTICS.CATEGORY, 'Auto parameters', eventLabel)

            this.$store.commit('RESET_INPUT_TANK_PARAMETERS')
            this.$router.push(this.getNextPageUri())
        },
        getNextPageUri() {
            if(this.$store.state.selectedShrimpIds.length > 0) {
                return this.$store.getters['getSummaryUri']
            }

            return '/shrimp-select'
        },
        showParametersSection() {
            this.$ga.event(ANALYTICS.CATEGORY, 'Water parameters', 'Show parameters section')
            this.wantsParameterSection = true
        }
    },
    computed: {
        /**
         * Show the parameters section by default if the user has explicitly asked for it
         * or if they have already entered some parameters.
         */
        shouldShowParametersSection() {
            return this.wantsParameterSection || this.$store.state.inputTankParameters.gh
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/mixins";

.page {
    display: flex;
    flex-direction: column;

    .button.skip {
        margin-bottom: 1em;
    }

    section {
        display: flex;
        flex-direction: column;
        margin-bottom: 1em;
        padding: 1em;
        background: #ecf5ed;

        h2 {
            margin-top: 0;
            margin-bottom: 0.25em;
        }
    }

    form {
        display: flex;
        flex-direction: column;
        margin-bottom: 1em;

        .parameter-fields-container {
            display: flex;
            flex-direction: column;

            @include breakpoint(tablet) {
                justify-content: space-evenly;
                flex-direction: row;
            }
        }

        .error {
            background: #f39b9b;
            padding: 1em;
            margin-bottom: 1em;
        }

        label {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 1em;
            font-size: 1.2em;

            @include breakpoint(tablet) {
                flex-direction: column;
            }

            span {
                font-weight: bold;
                margin-right: 2em;

                @include breakpoint(tablet) {
                    margin-right: 0;
                }
            }

            input[type="number"] {
                padding: 1em;
                font-size: 1.2em;
                width: 5em;
                border: 1px solid rgba(0, 0, 0, 0.2);
                text-align: center;
            }
        }
    }
}

.heading-container {
    display: flex;
    align-items: center;
    margin-bottom: 1em;

    .button-container {
        display: flex;
        flex: 1;
        justify-content: flex-end;
        align-items: center;

        .button {
            justify-self: flex-end;
        }
    }
}

.button-container {
    display: flex;
    justify-content: center;
}


.fade-resize-active, .fade-resize-leave-active {
  transition: opacity .25s, max-height .25s;
  max-height: 999px;
}
.fade-resize-enter, .fade-resize-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>